/**
 * Charkadam motion engine — page module `fx`, runs on every page (first load + every Swup visit).
 *
 * One vocabulary, applied everywhere (values mirror the MOTION LANGUAGE block in charkadam-tokens.css):
 *   headline   .ck-display/.ck-h1/.ck-h2 in <main> split into masked lines that rise in
 *   rise       [data-reveal] (default)        fade + rise by ck.motion.rise
 *   fade       [data-reveal="fade"]
 *   mask       [data-reveal="mask"]           frame wipes open bottom-up, the image inside settles from 1.12
 *   line       [data-reveal="line"]           a rule draws left to right
 *   stamp      [data-reveal="stamp"]          a rubber stamp thunks onto the page (scale down + settle)
 *   group      [data-reveal-group] (or legacy [data-ck-stagger]) children rise as one batch, 80ms apart
 *   count      [data-count]                   a whole number counts up once (text is restored exactly)
 *   parallax   [data-parallax="0.12"]         desktop only, scrubbed, tiny
 * Sections that predate the attributes are opted in through AUTO below, so no page is left static.
 *
 * Budget (ck.matchMedia): desktop = all of the above; mobile = rise/fade/group/headline with shorter
 * distance, no mask zoom, no parallax; reduced motion = everything shown instantly, nothing animates.
 * Nothing here pins, blocks scrolling, or delays access to content: every reveal is triggered on enter
 * and content is already interactive underneath it.
 */
(function () {
  var ck = (window.ck = window.ck || { pages: {}, assets: {} });
  var html = document.documentElement;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  ck.motion = {
    ease: { enter: 'expo.out', ui: 'power3.out', drift: 'sine.inOut' },
    dur: { short: 0.18, med: 0.6, long: 1.0 },
    stagger: 0.08,
    rise: 28,
    riseMobile: 18,
    zoom: 1.05
  };
  var M = ck.motion;

  // Legacy markup opted into the vocabulary (selector -> reveal type). Keep this list as the single
  // place where "which existing component animates how" is decided.
  var AUTO = [
    ['group', [
      '.ck-trust__list', '.ck-why__grid', '.ck-stats__list', '.ck-lcards', '.ck-contact__grid', '.ck-advisory__list',
      '.ck-faq__groups', '.ck-bridge__list', '.ck-related__grid', '.ck-trip-inclusions__grid', '.ck-trip-overview__cards',
      '.ck-split__list', '.ck-dhl__grid', '.ck-dready__grid', '.ck-dtrips__list', '.ck-footer__cols',
      '.ck-packing__picker', '.ck-trip-gallery__grid'
    ].join(',')],
    ['mask', [
      '.ck-split__media'
    ].join(',')],
    ['rise', [
      '.ck-lead', '.ck-page-cta__inner', '.ck-concierge__play', '.ck-cal__filters', '.ck-trips-filter',
      '.ck-fitness-table__table', '.ck-story__body', '.ck-trip-overview__lead', '.ck-rail__card'
    ].join(',')]
  ];

  var splits = [];
  var observers = [];
  var triggers = [];
  var mm = null;
  var progressOff = null;
  var magneticReady = false;

  function reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function showAll() {
    all('[data-reveal], [data-reveal-group]').forEach(function (el) { el.classList.add('is-in'); });
  }

  /* ---------- auto-tagging ---------- */
  function autoTag() {
    var main = document.getElementById('MainContent') || document;
    AUTO.forEach(function (entry) {
      all(entry[1], main).forEach(function (el) {
        if (el.closest('[data-reveal-skip]')) return;
        if (entry[0] === 'group') {
          if (!el.hasAttribute('data-reveal-group')) el.setAttribute('data-reveal-group', '');
        } else if (!el.hasAttribute('data-reveal') && !el.closest('[data-reveal-group]')) {
          el.setAttribute('data-reveal', entry[0]);
        }
      });
    });
    all('[data-ck-stagger]', main).forEach(function (el) { el.setAttribute('data-reveal-group', ''); });
    // Footer lives outside <main> but belongs to the same experience.
    all('.ck-footer__cols').forEach(function (el) { el.setAttribute('data-reveal-group', ''); });
  }

  /* ---------- headlines ---------- */
  function splitHeadline(el, aboveFold) {
    var anim = null;
    var inView = aboveFold;
    var big = el.matches('.ck-display, .ck-h1');
    var split = window.SplitText.create(el, {
      type: 'lines',
      mask: 'lines',
      linesClass: 'ck-line',
      autoSplit: true,
      onSplit: function (self) {
        anim = window.gsap.from(self.lines, {
          yPercent: 108,
          duration: big ? M.dur.long : 0.9,
          delay: aboveFold ? 0.15 : 0.02,
          ease: M.ease.enter,
          stagger: M.stagger,
          paused: !inView
        });
        el.classList.add('ck-split-done');
        return anim;
      }
    });
    splits.push(split);
    if (aboveFold) return;
    var io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      inView = true;
      if (anim) anim.play();
      io.disconnect();
    }, { rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    observers.push(io);
  }

  function initHeadlines() {
    var targets = all('main .ck-display, main .ck-h1, main .ck-h2');
    if (reduceMotion() || !window.gsap || !window.SplitText) {
      targets.forEach(function (el) { el.classList.add('ck-split-done'); });
      return;
    }
    window.gsap.registerPlugin(window.SplitText);
    var fontsReady = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    var timeout = new Promise(function (r) { setTimeout(r, 1800); });
    Promise.race([fontsReady, timeout]).then(function () {
      targets.forEach(function (el) {
        if (!el.isConnected) return;
        if (el.hasAttribute('data-ck-no-split') || !el.getClientRects().length || !el.textContent.trim()) {
          el.classList.add('ck-split-done');
          return;
        }
        var top = el.getBoundingClientRect().top;
        splitHeadline(el, top < window.innerHeight * 0.9);
      });
    });
  }

  /* ---------- scroll reveals ---------- */
  function revealSingles(c) {
    var gsap = window.gsap;
    var rise = c.desktop ? M.rise : M.riseMobile;
    all('[data-reveal]').forEach(function (el) {
      if (el.classList.contains('is-in')) return;
      var type = el.getAttribute('data-reveal') || 'rise';
      var tl = gsap.timeline({ paused: true, onComplete: function () { el.classList.add('is-in'); } });

      if (type === 'mask') {
        var img = el.querySelector('img, video, picture > img');
        tl.fromTo(el, { clipPath: 'inset(100% 0% 0% 0%)', opacity: 1 },
          { clipPath: 'inset(0% 0% 0% 0%)', duration: M.dur.long, ease: M.ease.enter, clearProps: 'clipPath' }, 0);
        if (img && c.desktop) tl.fromTo(img, { scale: 1.12 }, { scale: 1, duration: 1.2, ease: M.ease.enter, clearProps: 'transform' }, 0);
      } else if (type === 'line') {
        tl.fromTo(el, { scaleX: 0, transformOrigin: '0 50%', opacity: 1 },
          { scaleX: 1, duration: M.dur.long, ease: M.ease.enter, clearProps: 'transform' });
      } else if (type === 'stamp') {
        tl.fromTo(el, { opacity: 0, scale: 1.9 }, { opacity: 1, scale: 1, duration: 0.42, ease: 'back.out(2.4)', clearProps: 'opacity,transform' });
      } else if (type === 'fade') {
        tl.fromTo(el, { opacity: 0 }, { opacity: 1, duration: M.dur.med, ease: M.ease.ui, clearProps: 'opacity' });
      } else {
        tl.fromTo(el, { opacity: 0, y: rise }, { opacity: 1, y: 0, duration: M.dur.med + 0.1, ease: M.ease.enter, clearProps: 'opacity,transform' });
      }
      el.classList.add('is-armed');
      gsap.set(el, { opacity: type === 'mask' || type === 'line' ? 1 : 0 });
      if (type === 'mask') gsap.set(el, { clipPath: 'inset(100% 0% 0% 0%)' });
      triggers.push(window.ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: function () { el.classList.add('is-in'); tl.play(); }
      }));
    });
  }

  function revealGroups(c) {
    var gsap = window.gsap;
    var rise = c.desktop ? M.rise : M.riseMobile;
    all('[data-reveal-group]').forEach(function (group) {
      if (group.classList.contains('is-in')) return;
      var kids = Array.prototype.slice.call(group.children).filter(function (k) {
        return k.nodeType === 1 && !k.hidden && k.getClientRects().length;
      });
      group.classList.add('is-in');
      if (!kids.length) return;
      gsap.set(kids, { opacity: 0, y: rise });
      var batch = window.ScrollTrigger.batch(kids, {
        start: 'top 90%',
        once: true,
        onEnter: function (els) {
          gsap.to(els, {
            opacity: 1,
            y: 0,
            duration: M.dur.med + 0.1,
            ease: M.ease.enter,
            stagger: M.stagger,
            overwrite: true,
            clearProps: 'opacity,transform'
          });
        }
      });
      triggers.push.apply(triggers, batch);
    });
  }

  function counters(c) {
    all('[data-count]').forEach(function (el) {
      var final = el.textContent;
      var m = final.trim().match(/^(\d{1,4})$/);
      if (!m || c.reduce) return;
      var target = parseInt(m[1], 10);
      if (target < 2) return;
      var obj = { v: 0 };
      el.textContent = '0';
      triggers.push(window.ScrollTrigger.create({
        trigger: el,
        start: 'top 92%',
        once: true,
        onEnter: function () {
          window.gsap.to(obj, {
            v: target,
            duration: M.dur.long + 0.2,
            ease: M.ease.enter,
            onUpdate: function () { el.textContent = String(Math.round(obj.v)); },
            onComplete: function () { el.textContent = final; }
          });
        }
      }));
      // Restore on teardown so a Swup back-navigation never caches "0".
      triggers.push({ kill: function () { el.textContent = final; } });
    });
  }

  function parallax(c) {
    if (!c.desktop) return;
    all('[data-parallax]').forEach(function (el) {
      var amt = parseFloat(el.getAttribute('data-parallax')) || 0.1;
      var tween = window.gsap.fromTo(el, { yPercent: -amt * 50 }, {
        yPercent: amt * 50,
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement || el, start: 'top bottom', end: 'bottom top', scrub: true }
      });
      triggers.push(tween.scrollTrigger);
      triggers.push({ kill: function () { tween.kill(); window.gsap.set(el, { clearProps: 'transform' }); } });
    });
  }

  function initScroll() {
    var targets = document.querySelector('[data-reveal], [data-reveal-group], [data-count], [data-parallax]');
    if (!targets) return;
    if (reduceMotion() || !window.gsap || !ck.scrollReady) {
      showAll();
      return;
    }
    var token = {};
    initScroll.token = token;
    ck.scrollReady().then(function (ST) {
      if (initScroll.token !== token) return;
      if (!ST) { showAll(); return; }
      mm = ck.matchMedia(function (c) {
        if (c.reduce) { showAll(); return; }
        revealSingles(c);
        revealGroups(c);
        counters(c);
        parallax(c);
        return function () {
          triggers.forEach(function (t) { if (t && t.kill) t.kill(); });
          triggers = [];
        };
      });
      setTimeout(function () { ST.refresh(); }, 120);
    }).catch(showAll);
  }

  /* ---------- scroll progress ---------- */
  function initProgress() {
    var bar = document.querySelector('.ck-progress');
    if (!bar) return;
    var frame = 0;
    function update() {
      frame = 0;
      var max = document.documentElement.scrollHeight - window.innerHeight;
      var long = max > window.innerHeight * 2.2;
      bar.classList.toggle('is-on', long && window.scrollY > 80);
      bar.style.setProperty('--p', long ? Math.min(1, Math.max(0, window.scrollY / max)).toFixed(4) : 0);
    }
    function onScroll() { if (!frame) frame = requestAnimationFrame(update); }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    progressOff = function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }

  /* ---------- buttons: label roll + magnetic ---------- */
  function wrapLabels() {
    all('.ck-btn--primary, .ck-btn--secondary').forEach(function (btn) {
      if (btn.querySelector('.ck-btn__label') || btn.children.length) return;
      var text = btn.textContent.trim();
      if (!text) return;
      btn.textContent = '';
      var label = document.createElement('span');
      label.className = 'ck-btn__label';
      label.setAttribute('data-text', text);
      var inner = document.createElement('span');
      inner.textContent = text;
      label.appendChild(inner);
      btn.appendChild(label);
    });
  }
  // JS that later rewrites a button's text (booking CTA, quiz) can call ck.setButtonText(btn, text).
  ck.setButtonText = function (btn, text) {
    var label = btn && btn.querySelector('.ck-btn__label');
    if (!label) { if (btn) btn.textContent = text; return; }
    label.setAttribute('data-text', text);
    label.firstChild.textContent = text;
  };

  function initMagnetic() {
    if (magneticReady || !window.gsap) return;
    magneticReady = true;
    var SELECTOR = '.ck-btn--primary, .ck-btn--secondary, [data-ck-magnetic]';
    var current = null;
    var moveX = null;
    var moveY = null;

    function release() {
      if (!current) return;
      window.gsap.to(current, { x: 0, y: 0, scale: 1, duration: M.dur.med, ease: 'elastic.out(1, 0.5)', overwrite: true });
      current = null;
    }
    function clamp(v, l) { return Math.max(-l, Math.min(l, v)); }

    document.addEventListener('pointermove', function (event) {
      if (reduceMotion() || !finePointer.matches) { release(); return; }
      if (current && !current.isConnected) current = null;
      var btn = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
      if (btn && (btn.hidden || btn.disabled || btn.classList.contains('ck-btn--disabled'))) btn = null;
      if (btn !== current) {
        release();
        current = btn;
        if (btn) {
          moveX = window.gsap.quickTo(btn, 'x', { duration: 0.4, ease: M.ease.ui });
          moveY = window.gsap.quickTo(btn, 'y', { duration: 0.4, ease: M.ease.ui });
          if (!btn.hasAttribute('data-ck-magnetic')) {
            window.gsap.to(btn, { scale: 1.03, duration: M.dur.short, ease: M.ease.ui, overwrite: 'auto' });
          }
        }
      }
      if (!btn) return;
      var rect = btn.getBoundingClientRect();
      var cx = rect.left + rect.width / 2 - window.gsap.getProperty(btn, 'x');
      var cy = rect.top + rect.height / 2 - window.gsap.getProperty(btn, 'y');
      var card = btn.hasAttribute('data-ck-magnetic');
      moveX(clamp((event.clientX - cx) * (card ? 0.04 : 0.22), card ? 6 : 10));
      moveY(clamp((event.clientY - cy) * (card ? 0.06 : 0.3), card ? 5 : 8));
    });
    html.addEventListener('pointerleave', release);
  }

  ck.pages.fx = {
    init: function () {
      autoTag();
      wrapLabels();
      initHeadlines();
      initScroll();
      initProgress();
      initMagnetic();
    },
    destroy: function () {
      initScroll.token = null;
      if (mm) { mm.revert(); mm = null; }
      triggers.forEach(function (t) { if (t && t.kill) t.kill(); });
      triggers = [];
      splits.forEach(function (s) { s.revert(); });
      observers.forEach(function (io) { io.disconnect(); });
      splits = [];
      observers = [];
      if (progressOff) { progressOff(); progressOff = null; }
    }
  };
})();
